import { ElementRef, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { App } from '@df/core/app';
import { TimeoutService } from '@df/ng/timeout.service';
import { VisibilityService } from '@df/ui/common/visibility.service';
import { Media } from '@df/ui/display/media/interfaces';
import findIndex from 'lodash-es/findIndex';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { Subscription } from 'rxjs/internal/Subscription';

interface IObservedEntry {
    elementRef: ElementRef;
    subject: BehaviorSubject<boolean>;
    subscription: Subscription;
    visible: boolean;
    listPosition: number;
    identifier: string;
}

@Injectable({
    providedIn: 'root'
})
export class ProductListingVideoService {
    private playingElement = new BehaviorSubject<ElementRef>(undefined);
    private entries: IObservedEntry[] = [];
    private check = this.timeoutService.debounce(() => this._check(), 200);

    constructor(private app: App, private visibilityService: VisibilityService, private timeoutService: TimeoutService) {
        this.playingElement.pipe(takeUntilDestroyed()).subscribe(elementRef => {
            this.entries.forEach(entry => {
                const isPlaying = entry.elementRef === elementRef;

                entry.subject.next(isPlaying);
            });
        });
    }

    observe(elementRef: ElementRef, listPosition: number, identifier: string): BehaviorSubject<boolean> {
        const entry = this.toEntry(elementRef);

        if (entry) {
            return entry.subject;
        }

        const newEntry = {
            elementRef,
            subject: new BehaviorSubject(false),
            visible: false,
            listPosition,
            identifier,
            subscription: this.visibilityService.isInView(elementRef, { threshold: 0.5 }).subscribe(visible => {
                newEntry.visible = visible;

                if (!visible) {
                    this.app.events.broadcast(Media.Events.stop, identifier);
                    newEntry.subject.next(false);
                }

                this.check();
            })
        };

        this.entries.push(newEntry);

        return newEntry.subject;
    }

    notifyEnd(): void {
        const lastPlaying = this.playingElement.getValue();
        const lastPlayingEntry = lastPlaying && this.toEntry(lastPlaying);

        lastPlayingEntry?.subject?.next(false);

        this.nextVideo(lastPlayingEntry);
    }

    private toEntry(elementRef: ElementRef): IObservedEntry | undefined {
        return this.entries.find(entry => entry.elementRef === elementRef);
    }

    private _check() {
        const lastPlaying = this.playingElement.getValue();
        const lastPlayingEntry = lastPlaying && this.toEntry(lastPlaying);

        this.nextVideo(lastPlayingEntry);
    }

    private nextVideo(lastPlayingEntry: IObservedEntry): void {
        const minPosition = lastPlayingEntry ? lastPlayingEntry.listPosition + 1 : 0;
        const nextVideo =
            this.entries.find(entry => entry.listPosition >= minPosition && entry.visible) || this.entries.find(entry => entry.visible);

        // if there is no video playing or the next video is the same as the last playing video
        if (nextVideo && (!lastPlayingEntry?.subject?.value || nextVideo?.elementRef === lastPlayingEntry?.elementRef)) {
            this.playingElement.next(nextVideo?.elementRef);
        }
    }

    unsubscribe(elementRef: ElementRef): void {
        const index = findIndex(this.entries, entry => entry.elementRef === elementRef);

        if (index > -1) {
            const lastPlaying = this.playingElement.getValue();
            const lastPlayingEntry = lastPlaying && this.toEntry(lastPlaying);

            this.entries.splice(index, 1).forEach(entry => {
                if (entry.elementRef === lastPlayingEntry?.elementRef) {
                    this.playingElement.next(undefined);
                    entry.subject.next(false);
                }

                entry.subscription.unsubscribe();
            });

            this.check();
        }
    }
}
